import noAssetsImg from "assets/img/no-assets.svg"

import { FC, useState, useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"

import Text from "components/Text/Text"
import { Guid, IApiLink } from "@/types"
import Spinner from "components/Spinner/Spinner"
import toastMessages from "stores/toastMessages"
import AssetService from "services/AssetService"
import { Flex } from "components/Flex"
import Button from "components/Button/Button"
import AssetCollectionModal from "./AssetCollectionModal"
import useAssetCollection from "hooks/useAssetCollection"
import AssetCollectionTable from "./AssetCollectionTable"

interface IAssetCollectionProps {
    assetId: Guid
    linkGetAssetCollection?: IApiLink
    linkUpdateAssetCollection?: IApiLink
    onCollectionChanged: (hasConnectedAssets: boolean) => void
}

const AssetCollection: FC<IAssetCollectionProps> = ({
    assetId,
    linkGetAssetCollection,
    linkUpdateAssetCollection,
    onCollectionChanged,
}) => {
    const { t } = useTranslation()
    const [showAssetCollectionModal, setShowAssetCollectionModal] = useState(false)
    const { assetCollection, fetchAssetCollection } = useAssetCollection(linkGetAssetCollection)
    const connectedAssets = assetCollection?.assets || null
    const isLoading = !connectedAssets

    useEffect(() => {
        fetchAssetCollection()
    }, [fetchAssetCollection])

    const handleUpdateAssetCollection = useCallback(async (assetIds: Guid[]) => {
        if (!connectedAssets?.length && !assetIds.length) return

        // we have to delete the collection if there are no assets
        const link = assetIds.length > 0 ? linkUpdateAssetCollection : assetCollection?.linkDelete
        if (!link) {
            throw new Error("A link to update/delete the asset collection not found")
        }

        try {
            if (assetIds.length > 0) {
                await AssetService.updateAssetCollection(link, assetIds)
            } else {
                await AssetService.deleteAssetCollection(link)
            }
            fetchAssetCollection()
            onCollectionChanged(assetIds.length > 0)
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [assetCollection?.linkDelete, connectedAssets?.length, fetchAssetCollection, linkUpdateAssetCollection, onCollectionChanged])

    return (
        <>
            <Flex alignItems="center" justifyContent="space-between" gap="1.6rem" className="mb-3">
                <Text type="h3">{t("asset.connectedAssets")}</Text>
                {!!assetCollection?.assets.length && !!linkUpdateAssetCollection &&
                    <Button
                        onClick={() => setShowAssetCollectionModal(true)}
                        theme="secondary"
                        label={t("asset.manageCollection")}
                    />
                }
            </Flex>
            {!isLoading && !!connectedAssets && connectedAssets.length > 0 &&
                <div>
                    <AssetCollectionTable connectedAssets={connectedAssets} />
                </div>
            }
            {!isLoading && !!connectedAssets && connectedAssets.length === 0 &&
                <Flex flow="column" gap="3.2rem" alignItems="center" className="text-center">
                    <img src={noAssetsImg} alt="" />
                    <div>{t("asset.assetNotPartOfCollection")}</div>
                    {!!linkUpdateAssetCollection &&
                        <Button
                            onClick={() => setShowAssetCollectionModal(true)}
                            theme="secondary"
                            label={t("asset.createNewCollection")}
                        />
                    }
                </Flex>
            }
            {isLoading && <Spinner className="py-4" />}
            {showAssetCollectionModal &&
                <AssetCollectionModal
                    assetId={assetId}
                    connectedAssets={connectedAssets || []}
                    onSave={handleUpdateAssetCollection}
                    onClose={() => setShowAssetCollectionModal(false)}
                />
            }
        </>
    )
}

export default AssetCollection
